<template>
  <!-- «Reprocesar todo»: dto.reprocessAll (por defecto apagado). Al encenderlo, un popover explica las
       consecuencias y pide confirmarlo; encendido se ve en ámbar. Se guarda con la definición y el motor lo
       apaga solo al terminar la pasada. -->
  <div ref="rootRef" class="rp" @keydown.esc="onEsc">
    <button ref="btnRef" type="button" class="tgl rp-tgl" :class="{ on: dto.reprocessAll }" aria-haspopup="dialog" :aria-expanded="isOpen"
            :title="t('rp.title')" @click="isOpen ? close(false) : open()">
      <span class="tgl-track"><span class="tgl-knob" /></span>
      {{ t('rp.toggle') }}
    </button>
    <Transition name="vem-pop">
      <div v-if="isOpen" class="rp-pop" role="dialog" :aria-label="t('rp.title')">
        <h4 class="rp-h">{{ t('rp.title') }}</h4>
        <p class="rp-p">{{ t('rp.what', { n: fmt(total) }) }}</p>
        <ul class="rp-list">
          <li v-for="k in CONSEQUENCES" :key="k">{{ t('rp.c.' + k) }}</li>
        </ul>
        <p class="hint">{{ t(dto.reprocessAll ? 'rp.onHint' : 'rp.saveHint') }}</p>
        <div class="rp-btns">
          <button v-if="dto.reprocessAll" ref="mainRef" type="button" class="btn" @click="set(false)">{{ t('rp.off') }}</button>
          <template v-else>
            <button ref="mainRef" type="button" class="btn warn" @click="set(true)">{{ t('rp.on') }}</button>
            <button type="button" class="btn ghost" @click="close(true)">{{ t('rp.cancel') }}</button>
          </template>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { t } from '@vem/i18n.js';
import { formById } from '@vem/catalog.js';

const CONSEQUENCES = ['values', 'manual', 'removed', 'time', 'resume'];

const props = defineProps({
  /** dto del editor: se edita reprocessAll. */
  dto: { type: Object, required: true },
});

const isOpen = ref(false);
const rootRef = ref(null);
const btnRef = ref(null);
const mainRef = ref(null);

/** Encuestas de los formularios fuente activos (las que se volverían a pasar). */
const total = computed(() => props.dto.sources.filter((s) => s.enabled !== false)
  .reduce((n, s) => n + (formById(s.questId)?.responses || 0), 0));
const fmt = (n) => new Intl.NumberFormat('es-ES').format(n);

function open() {
  isOpen.value = true;
  nextTick(() => mainRef.value?.focus());
}

function close(refocus) {
  isOpen.value = false;
  if (refocus) nextTick(() => btnRef.value?.focus());
}

function set(v) {
  props.dto.reprocessAll = v;
  close(true);
}

function onEsc(e) {
  if (!isOpen.value) return;
  e.stopPropagation();
  close(true);
}

function onDocDown(e) {
  if (isOpen.value && rootRef.value && !rootRef.value.contains(e.target)) close(false);
}

onMounted(() => document.addEventListener('pointerdown', onDocDown));
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocDown));
</script>

<style>
.vem-root .rp { position: relative; }
.vem-root .rp-tgl { font-size: 12.5px; }
.vem-root .rp-tgl.on { color: var(--amber); font-weight: 600; }
.vem-root .rp-tgl.on .tgl-track { background: var(--amber); }
.vem-root .rp-pop {
  position: absolute; z-index: 20; top: calc(100% + 8px); left: 0; width: 380px; max-width: calc(100vw - 32px);
  background: var(--panel); border: 1px solid var(--amber-bd); border-top: 3px solid var(--amber); border-radius: 12px;
  padding: 12px 14px; box-shadow: 0 14px 36px var(--shadow);
}
.vem-root .rp-h { margin: 0 0 6px; font-size: 13.5px; color: var(--amber); }
.vem-root .rp-p { margin: 0; font-size: 12.5px; line-height: 1.45; color: var(--tx); }
.vem-root .rp-list { margin: 8px 0 0; padding-left: 18px; font-size: 12px; line-height: 1.45; color: var(--tx2); }
.vem-root .rp-list li + li { margin-top: 4px; }
.vem-root .rp-pop > .hint { margin: 10px 0 0; }
.vem-root .rp-btns { display: flex; gap: 8px; margin-top: 12px; }
.vem-root .btn.warn { background: var(--amber); border-color: var(--amber); color: #fff; }
</style>
